import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Drawer, Empty, Flex, Input, Spin, Tag, Typography, message } from "antd";
import {
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import { PresupuestoIcon, BoxSeam, SellerIcon, ShareIcon } from "@/components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { axiosInstance, formatPrice, resolveStoreId } from "@/utilities";
import { API_URL_QUOTES } from "@/services/XCONS_ENV";
import {
  OrderBrief,
  QuoteBrief,
  contextApi,
  orderBadgeStyle,
  orderStatusDot,
  quoteStatusTag,
  translateStatusLabel,
} from "@/services/context.api";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Text } = Typography;

export type ContextPanelType = "quotes" | "orders" | null;

const GREEN = "#00733B";
const updated = (iso?: string) => (iso ? `Updated ${dayjs(iso).fromNow()}` : "");

/** Side panel con presupuestos o pedidos del contacto en el store (accesos del head del chat). */
export const ContextPanel = ({
  type,
  onClose,
  contactId,
  userId,
  storeCode,
  contactName,
  onShareQuote,
  recargar,
}: {
  type: ContextPanelType;
  onClose: () => void;
  contactId?: number;
  userId?: number;
  storeCode: string;
  contactName: string;
  /** Preescribe en el chat el mensaje con el link del presupuesto (N5). */
  onShareQuote?: (quote: QuoteBrief) => void;
  /** Cambia cuando una nota nombra un presupuesto nuevo: abierto, el panel vuelve a cargar (#639). */
  recargar?: string;
}) => {
  const [quotes, setQuotes] = useState<QuoteBrief[]>([]);
  const [orders, setOrders] = useState<OrderBrief[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // Lo que se abre: el panel, otro contacto, otro store. Una recarga por un presupuesto nuevo (#639)
  // no cuenta: no borra la búsqueda ni muestra el «cargando» sobre la lista que ya está.
  const abierto = useRef<string | null>(null);
  useEffect(() => {
    if (!type) {
      abierto.current = null;
      return;
    }
    const clave = `${type}:${contactId ?? ""}:${userId ?? ""}:${storeCode}`;
    const recarga = abierto.current === clave;
    abierto.current = clave;
    if (!recarga) {
      setQ("");
      setLoading(true);
    }
    let active = true;
    const p =
      type === "quotes"
        ? contactId
          ? contextApi.quotesByContact(contactId, storeCode).then((r) => active && setQuotes(r))
          : Promise.resolve()
        : userId
          ? contextApi.ordersByContact(userId, storeCode).then((r) => active && setOrders(r))
          : Promise.resolve();
    p.catch(() => {}).finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [type, contactId, userId, storeCode, recargar]);

  const threeMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d;
  }, []);

  const filteredQuotes = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? quotes.filter((x) => `#${x.id} ${x.name}`.toLowerCase().includes(s)) : quotes;
  }, [quotes, q]);

  const recentQuotes = useMemo(
    () => filteredQuotes.filter((x) => x.updatedAt && new Date(x.updatedAt) >= threeMonthsAgo),
    [filteredQuotes, threeMonthsAgo],
  );
  const olderQuotes = useMemo(
    () => filteredQuotes.filter((x) => !x.updatedAt || new Date(x.updatedAt) < threeMonthsAgo),
    [filteredQuotes, threeMonthsAgo],
  );
  const filteredOrders = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? orders.filter((x) => `#${x.incrementId}`.toLowerCase().includes(s)) : orders;
  }, [orders, q]);
  const recentOrders = useMemo(
    () => filteredOrders.filter((x) => x.createdAt && new Date(x.createdAt) >= threeMonthsAgo),
    [filteredOrders, threeMonthsAgo],
  );
  const olderOrders = useMemo(
    () => filteredOrders.filter((x) => !x.createdAt || new Date(x.createdAt) < threeMonthsAgo),
    [filteredOrders, threeMonthsAgo],
  );

  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const typeLabel = type === "quotes" ? "Presupuestos" : type === "orders" ? "Pedidos" : "";
  const title =
    type === "quotes" ? `Presupuestos en ${cap(storeCode)}` : type === "orders" ? `Pedidos en ${cap(storeCode)}` : "";
  /**
   * Comparte el presupuesto por el chat abierto (N5).
   *
   * A diferencia del mismo botón en la ficha del presupuesto (N6), acá **no hay nada que preguntar**:
   * el canal, la conversación y el contacto son los del chat en el que ya estás. Sólo se preescribe
   * el mensaje y el operador aprieta enviar.
   */
  const shareQuote = (qt: QuoteBrief) => {
    if (!qt.shareHash || !qt.storeLink) {
      message.error("El presupuesto no tiene link público todavía");
      return;
    }
    onShareQuote?.(qt);
    onClose();
  };

  // Bug 30/33: ícono de sección correcto (documento para Quotes, pedido para Orders).
  const icon =
    type === "quotes" ? <PresupuestoIcon style={{ color: GREEN }} /> : <BoxSeam style={{ color: GREEN }} />;

  // Bug 37: copiar al portapapeles el link directo al presupuesto/pedido.
  const copyLink = (path: string) => {
    void navigator.clipboard
      .writeText(`${window.location.origin}${path}`)
      .then(() => message.success("Link copiado"))
      .catch(() => message.error("No se pudo copiar el link"));
  };

  // #116: crear un presupuesto para el customer del chat (customerId = userId del contacto).
  // El create pide storeId (Magento); lo resolvemos desde el storeCode (ver `resolveStoreId`).
  const handleCreateQuote = async () => {
    if (!userId) {
      message.warning("El contacto no tiene customer asociado");
      return;
    }
    try {
      const storeId = await resolveStoreId(storeCode);
      if (!storeId) {
        message.error("No se pudo resolver el store del presupuesto");
        return;
      }
      const { data } = await axiosInstance.post(API_URL_QUOTES, { storeId, customerId: userId });
      const newId = data?.quoteId;
      if (!newId) {
        message.error("No se recibió el id del presupuesto");
        return;
      }
      window.open(`/quotes/${newId}`, "_blank");
    } catch (e: any) {
      message.error(`No se pudo crear el presupuesto: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  };

  return (
    <Drawer
      title={title}
      placement="right"
      width={500}
      open={!!type}
      onClose={onClose}
      closable={false}
      extra={<Button type="text" icon={<CloseOutlined />} onClick={onClose} />}
      styles={{ body: { background: "#ffffff", padding: 12, display: "flex", flexDirection: "column" } }}
    >
      <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff", display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        <Flex
          align="center"
          justify="space-between"
          style={{ background: "#f5f5f5", padding: "8px 12px", borderBottom: "1px solid #f0f0f0", borderRadius: "8px 8px 0 0" }}
        >
          <Flex align="center" gap={8}>
            {icon}
            <Text strong style={{ color: GREEN }}>
              {typeLabel}
            </Text>
          </Flex>
          {type === "quotes" && (
            <Button type="primary" size="small" icon={<PlusOutlined />} style={{ background: GREEN }} onClick={handleCreateQuote} />
          )}
        </Flex>
        <div style={{ padding: "12px 12px 0" }}>
          <Input allowClear placeholder="Search" prefix={<SearchOutlined />} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {loading ? (
        <div style={{ textAlign: "center", paddingTop: 40 }}>
          <Spin />
        </div>
      ) : type === "quotes" ? (
        filteredQuotes.length === 0 ? (
          <Empty description="Sin presupuestos" />
        ) : (
          <Flex vertical gap={10}>
            {recentQuotes.length > 0 && (
              <Text style={{ fontSize: 12, fontWeight: 600, color: "#8c8c8c" }}>
                Últimos 3 meses
              </Text>
            )}
            {recentQuotes.map((qt) => {
              const st = quoteStatusTag(qt.state);
              return (
                <Card
                  key={qt.id}
                  size="small"
                  hoverable
                  onClick={() => window.open(`/quotes/${qt.id}`, "_blank")}
                  style={{ border: "1px solid #f0f0f0", borderRadius: 6 }}
                  styles={{ body: { padding: 8 } }}
                >
                  <Flex justify="space-between" align="center" gap={8}>
                    <Flex align="center" gap={6} wrap="wrap">
                      <Button
                        size="small"
                        icon={<ShareIcon />}
                        title="Compartir por este chat"
                        style={{ width: 24, height: 24, padding: 0, minWidth: 24 }}
                        onClick={(e) => { e.stopPropagation(); shareQuote(qt); }}
                      />
                      <Text strong style={{ color: "#2b8058" }}>#{qt.id} - {qt.name}</Text>
                      {st && (
                        <Tag style={{ background: st.bg, color: st.color, borderColor: st.borderColor }}>{st.label}</Tag>
                      )}
                    </Flex>
                    <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.65)", whiteSpace: "nowrap" }}>
                      {qt.updatedAt ? dayjs(qt.updatedAt).format("DD/MM/YY") : ""}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="start" style={{ marginTop: 4 }}>
                    <Flex vertical gap={4}>
                      {qt.storeCode && (
                        <Flex align="center" gap={4}>
                            <ShopOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{qt.storeCode}</Text>
                          </Flex>
                      )}
                      {qt.sellerCode && (
                        <Flex align="center" gap={4}>
                            <SellerIcon style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{qt.sellerCode}</Text>
                          </Flex>
                      )}
                    </Flex>
                    {qt.updatedAt && (
                      <Flex vertical align="flex-end">
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>Actualizado</Text>
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{dayjs(qt.updatedAt).fromNow()}</Text>
                      </Flex>
                    )}
                  </Flex>
                  <Flex justify="space-between" align="center" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                    <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.45)" }}>Total</Text>
                    <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.88)" }}>{formatPrice(qt.total)}</Text>
                  </Flex>
                </Card>
              );
            })}
            {olderQuotes.length > 0 && (
              <Text style={{ fontSize: 12, fontWeight: 600, color: "#8c8c8c", marginTop: 4 }}>
                Más antiguos
              </Text>
            )}
            {olderQuotes.map((qt) => {
              const st = quoteStatusTag(qt.state);
              return (
                <Card
                  key={qt.id}
                  size="small"
                  hoverable
                  onClick={() => window.open(`/quotes/${qt.id}`, "_blank")}
                  style={{ border: "1px solid #f0f0f0", borderRadius: 6 }}
                  styles={{ body: { padding: 8 } }}
                >
                  <Flex justify="space-between" align="center" gap={8}>
                    <Flex align="center" gap={6} wrap="wrap">
                      <Button
                        size="small"
                        icon={<ShareIcon />}
                        title="Compartir por este chat"
                        style={{ width: 24, height: 24, padding: 0, minWidth: 24 }}
                        onClick={(e) => { e.stopPropagation(); shareQuote(qt); }}
                      />
                      <Text strong style={{ color: "#2b8058" }}>#{qt.id} - {qt.name}</Text>
                      {st && (
                        <Tag style={{ background: st.bg, color: st.color, borderColor: st.borderColor }}>{st.label}</Tag>
                      )}
                    </Flex>
                    <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.65)", whiteSpace: "nowrap" }}>
                      {qt.updatedAt ? dayjs(qt.updatedAt).format("DD/MM/YY") : ""}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="start" style={{ marginTop: 4 }}>
                    <Flex vertical gap={4}>
                      {qt.storeCode && (
                        <Flex align="center" gap={4}>
                            <ShopOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{qt.storeCode}</Text>
                          </Flex>
                      )}
                      {qt.sellerCode && (
                        <Flex align="center" gap={4}>
                            <SellerIcon style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{qt.sellerCode}</Text>
                          </Flex>
                      )}
                    </Flex>
                    {qt.updatedAt && (
                      <Flex vertical align="flex-end">
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>Actualizado</Text>
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{dayjs(qt.updatedAt).fromNow()}</Text>
                      </Flex>
                    )}
                  </Flex>
                  <Flex justify="space-between" align="center" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                    <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.45)" }}>Total</Text>
                    <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.88)" }}>{formatPrice(qt.total)}</Text>
                  </Flex>
                </Card>
              );
            })}
          </Flex>
        )
      ) : filteredOrders.length === 0 ? (
        <Empty description="Sin pedidos" />
      ) : (
        <Flex vertical gap={10}>
          {recentOrders.length > 0 && (
            <Text style={{ fontSize: 12, fontWeight: 600, color: "#8c8c8c" }}>
              Últimos 3 meses
            </Text>
          )}
          {recentOrders.map((o) => {
            const dot = orderStatusDot(o.status);
            return (
              <Card
                key={o.orderId}
                size="small"
                hoverable
                onClick={() => window.open(`/orders/${o.orderId}`, "_blank")}
                style={{ border: "1px solid #f0f0f0", borderRadius: 6 }}
                styles={{ body: { padding: 8 } }}
              >
                <Flex justify="space-between" align="center" gap={8}>
                  <Flex align="center" gap={6} wrap="wrap">
                    <Button
                      size="small"
                      icon={<ShareIcon />}
                      style={{ width: 24, height: 24, padding: 0, minWidth: 24 }}
                      onClick={(e) => { e.stopPropagation(); copyLink(`/orders/${o.orderId}`); }}
                    />
                    <Text strong style={{ color: "#2b8058" }}>#{o.incrementId}{o.name ? ` - ${o.name}` : ""}</Text>
                    <span style={{ width: 8, height: 8, borderRadius: 4, background: dot.color, display: "inline-block" }} />
                    <Text style={{ fontSize: 12 }}>{dot.label}</Text>
                  </Flex>
                  <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.65)", whiteSpace: "nowrap" }}>
                    {o.createdAt ? dayjs(o.createdAt).format("DD/MM/YY") : ""}
                  </Text>
                </Flex>
                <Flex justify="space-between" align="start" style={{ marginTop: 4 }}>
                  <Flex vertical gap={4}>
                    {o.storeCode && (
                      <Flex align="center" gap={4}>
                        <ShopOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{o.storeCode}</Text>
                      </Flex>
                    )}
                    {o.sellerCode && (
                      <Flex align="center" gap={4}>
                        <SellerIcon style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{o.sellerCode}</Text>
                      </Flex>
                    )}
                  </Flex>
                  {(o.updatedAt ?? o.createdAt) && (
                    <Flex vertical align="flex-end">
                      <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>Actualizado</Text>
                      <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{dayjs(o.updatedAt ?? o.createdAt).fromNow()}</Text>
                    </Flex>
                  )}
                </Flex>
                {(o.invoiceStatus || o.shipments.length > 0) && (
                  <Flex vertical gap={4} style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                    <Flex justify="space-between" align="center">
                      <Text style={{ fontSize: 12, fontWeight: 600 }}>Factura del pedido</Text>
                      {o.invoiceStatus && (() => {
                        const bs = orderBadgeStyle(o.invoiceStatus);
                        return (
                          <span style={{ backgroundColor: bs.bg, color: bs.color, border: `1px solid ${bs.borderColor}`, borderRadius: 6, padding: "0 8px", fontSize: 12, lineHeight: 1, height: 22, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" }}>
                            {translateStatusLabel(o.invoiceStatus)}
                          </span>
                        );
                      })()}
                    </Flex>
                    {o.shipments.map((s, i) => {
                      const bs = orderBadgeStyle(s.status);
                      return (
                        <Flex key={i} justify="space-between" align="center">
                          <Flex align="center" gap={6}>
                            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{i + 1}</Text>
                            <TruckOutlined style={{ color: "#8c8c8c" }} />
                            <Text style={{ fontSize: 12, fontWeight: 600 }}>{s.deliveryMethod}</Text>
                          </Flex>
                          <span style={{ backgroundColor: bs.bg, color: bs.color, border: `1px solid ${bs.borderColor}`, borderRadius: 6, padding: "0 8px", fontSize: 12, lineHeight: 1, height: 22, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" }}>
                            {translateStatusLabel(s.status)}
                          </span>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
                <Flex justify="space-between" align="center" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.45)" }}>Total</Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.88)" }}>{formatPrice(o.grandTotal)}</Text>
                </Flex>
              </Card>
            );
          })}
          {olderOrders.length > 0 && (
            <Text style={{ fontSize: 12, fontWeight: 600, color: "#8c8c8c", marginTop: 4 }}>
              Más antiguos
            </Text>
          )}
          {olderOrders.map((o) => {
            const dot = orderStatusDot(o.status);
            return (
              <Card
                key={o.orderId}
                size="small"
                hoverable
                onClick={() => window.open(`/orders/${o.orderId}`, "_blank")}
                style={{ border: "1px solid #f0f0f0", borderRadius: 6 }}
                styles={{ body: { padding: 8 } }}
              >
                <Flex justify="space-between" align="center" gap={8}>
                  <Flex align="center" gap={6} wrap="wrap">
                    <Button
                      size="small"
                      icon={<ShareIcon />}
                      style={{ width: 24, height: 24, padding: 0, minWidth: 24 }}
                      onClick={(e) => { e.stopPropagation(); copyLink(`/orders/${o.orderId}`); }}
                    />
                    <Text strong style={{ color: "#2b8058" }}>#{o.incrementId}{o.name ? ` - ${o.name}` : ""}</Text>
                    <span style={{ width: 8, height: 8, borderRadius: 4, background: dot.color, display: "inline-block" }} />
                    <Text style={{ fontSize: 12 }}>{dot.label}</Text>
                  </Flex>
                  <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.65)", whiteSpace: "nowrap" }}>
                    {o.createdAt ? dayjs(o.createdAt).format("DD/MM/YY") : ""}
                  </Text>
                </Flex>
                <Flex justify="space-between" align="start" style={{ marginTop: 4 }}>
                  <Flex vertical gap={4}>
                    {o.storeCode && (
                      <Flex align="center" gap={4}>
                        <ShopOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{o.storeCode}</Text>
                      </Flex>
                    )}
                    {o.sellerCode && (
                      <Flex align="center" gap={4}>
                        <SellerIcon style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                        <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{o.sellerCode}</Text>
                      </Flex>
                    )}
                  </Flex>
                  {(o.updatedAt ?? o.createdAt) && (
                    <Flex vertical align="flex-end">
                      <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>Actualizado</Text>
                      <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{dayjs(o.updatedAt ?? o.createdAt).fromNow()}</Text>
                    </Flex>
                  )}
                </Flex>
                {(o.invoiceStatus || o.shipments.length > 0) && (
                  <Flex vertical gap={4} style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                    <Flex justify="space-between" align="center">
                      <Text style={{ fontSize: 12, fontWeight: 600 }}>Factura del pedido</Text>
                      {o.invoiceStatus && (() => {
                        const bs = orderBadgeStyle(o.invoiceStatus);
                        return (
                          <span style={{ backgroundColor: bs.bg, color: bs.color, border: `1px solid ${bs.borderColor}`, borderRadius: 6, padding: "0 8px", fontSize: 12, lineHeight: 1, height: 22, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" }}>
                            {translateStatusLabel(o.invoiceStatus)}
                          </span>
                        );
                      })()}
                    </Flex>
                    {o.shipments.map((s, i) => {
                      const bs = orderBadgeStyle(s.status);
                      return (
                        <Flex key={i} justify="space-between" align="center">
                          <Flex align="center" gap={6}>
                            <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>{i + 1}</Text>
                            <TruckOutlined style={{ color: "#8c8c8c" }} />
                            <Text style={{ fontSize: 12, fontWeight: 600 }}>{s.deliveryMethod}</Text>
                          </Flex>
                          <span style={{ backgroundColor: bs.bg, color: bs.color, border: `1px solid ${bs.borderColor}`, borderRadius: 6, padding: "0 8px", fontSize: 12, lineHeight: 1, height: 22, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" }}>
                            {translateStatusLabel(s.status)}
                          </span>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
                <Flex justify="space-between" align="center" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.45)" }}>Total</Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.88)" }}>{formatPrice(o.grandTotal)}</Text>
                </Flex>
              </Card>
            );
          })}
        </Flex>
        )}
        </div>
      </div>
    </Drawer>
  );
};
